import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay, switchMap } from 'rxjs/operators';
import { LoginRequest, LoginResponse, RefreshTokenResponse, ApiUser, USER_ROLES } from '../models/auth.models';

/**
 * Mock Authentication Service
 * Provides mock API responses for development and testing
 * Simulates real authentication behavior with delays
 */
@Injectable({
  providedIn: 'root'
})
export class MockAuthService {
  
  // Mock users database
  private readonly mockUsers: Array<ApiUser & { password: string }> = [
    {
      userId: 1,
      userType: 'Admin',
      email: 'admin@showlab.com',
      password: 'admin123',
      firstName: 'Admin',
      lastName: 'User',
      stageName: 'Admin',
      mobileNumber: '1234567890',
      birthday: '1990-01-01T00:00:00',
      createdDate: '2024-01-01T00:00:00Z',
      lastLoginDate: new Date().toISOString(),
      roleId: 1,
      roleName: USER_ROLES.ADMIN,
      businessId: 1
    },
    {
      userId: 2,
      userType: 'User',
      email: 'user@showlab.com',
      password: 'user123',
      firstName: 'Regular',
      lastName: 'User',
      stageName: 'RegUser',
      mobileNumber: '0987654321',
      birthday: '1995-01-01T00:00:00',
      createdDate: '2024-01-15T00:00:00Z',
      lastLoginDate: new Date().toISOString(),
      roleId: 3,
      roleName: USER_ROLES.USER,
      businessId: 2
    },
    {
      userId: 3,
      userType: 'Business',
      email: 'test@example.com',
      password: 'test123',
      firstName: 'Test',
      lastName: 'Account',
      stageName: 'TestAcc',
      mobileNumber: '5555555555',
      birthday: '1992-01-01T00:00:00',
      createdDate: '2024-02-01T00:00:00Z',
      lastLoginDate: new Date().toISOString(),
      roleId: 2,
      roleName: USER_ROLES.BUSINESS,
      businessId: 3
    }
  ];

  // Mock tokens storage
  private validRefreshTokens = new Set<string>();

  /**
   * Mock login endpoint
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return of(null).pipe(
      delay(1000), // Simulate network delay
      switchMap(() => {
        const user = this.mockUsers.find(u => 
          u.email === credentials.email && u.password === credentials.password
        );

        if (!user) {
          return throwError(() => ({
            message: 'Invalid email or password',
            code: 'INVALID_CREDENTIALS',
            statusCode: 401,
            timestamp: new Date().toISOString()
          }));
        }

        // Generate mock tokens
        const accessToken = this.generateMockToken('access', user.userId.toString());
        const refreshToken = this.generateMockToken('refresh', user.userId.toString());
        
        // Store refresh token
        this.validRefreshTokens.add(refreshToken);

        // Update last login
        user.lastLoginDate = new Date().toISOString();

        const response: LoginResponse = {
          success: true,
          message: 'login successful',
          token: accessToken,
          refreshToken: refreshToken,
          user: {
            userId: user.userId,
            userType: user.userType,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            stageName: user.stageName,
            mobileNumber: user.mobileNumber,
            birthday: user.birthday,
            createdDate: user.createdDate,
            lastLoginDate: user.lastLoginDate,
            roleId: user.roleId,
            roleName: user.roleName,
            businessId: user.businessId
          }
        };

        return of(response);
      })
    );
  }

  /**
   * Mock refresh token endpoint
   */
  refreshToken(refreshToken: string): Observable<RefreshTokenResponse> {
    return of(null).pipe(
      delay(500), // Simulate network delay
      switchMap(() => {
        if (!this.validRefreshTokens.has(refreshToken)) {
          return throwError(() => ({
            message: 'Invalid refresh token',
            code: 'INVALID_REFRESH_TOKEN',
            statusCode: 401,
            timestamp: new Date().toISOString()
          }));
        }

        // Extract user ID from token (in real app, this would be JWT verification)
        const userId = this.extractUserIdFromToken(refreshToken);
        
        // Generate new tokens
        const newAccessToken = this.generateMockToken('access', userId);
        const newRefreshToken = this.generateMockToken('refresh', userId);

        // Update refresh token storage
        this.validRefreshTokens.delete(refreshToken);
        this.validRefreshTokens.add(newRefreshToken);

        const response: RefreshTokenResponse = {
          success: true,
          message: 'Token refreshed successfully',
          token: newAccessToken,
          refreshToken: newRefreshToken
        };

        return of(response);
      })
    );
  }

  /**
   * Mock get user profile endpoint
   */
  getUserProfile(userId: number): Observable<ApiUser> {
    return of(null).pipe(
      delay(300),
      switchMap(() => {
        const user = this.mockUsers.find(u => u.userId === userId);

        if (!user) {
          return throwError(() => ({
            message: 'User not found',
            code: 'USER_NOT_FOUND',
            statusCode: 404,
            timestamp: new Date().toISOString()
          }));
        }

        const userProfile: ApiUser = {
          userId: user.userId,
          userType: user.userType,
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          stageName: user.stageName,
          mobileNumber: user.mobileNumber,
          birthday: user.birthday,
          createdDate: user.createdDate,
          lastLoginDate: user.lastLoginDate,
          roleId: user.roleId,
          roleName: user.roleName,
          businessId: user.businessId
        };

        return of(userProfile);
      })
    );
  }

  /**
   * Mock logout endpoint
   */
  logout(refreshToken?: string): Observable<void> {
    return of(null).pipe(
      delay(200),
      switchMap(() => {
        if (refreshToken) {
          this.validRefreshTokens.delete(refreshToken);
        }
        return of(undefined);
      })
    );
  }

  /**
   * Get mock users for testing (development only)
   */
  getMockUsers(): Array<{ email: string; password: string; roleName: string }> {
    return this.mockUsers.map(user => ({
      email: user.email,
      password: user.password,
      roleName: user.roleName
    }));
  }

  // Private helper methods
  private generateMockToken(type: 'access' | 'refresh', userId: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2);
    return `mock_${type}_${userId}_${timestamp}_${random}`;
  }

  private extractUserIdFromToken(token: string): string {
    // In a real app, this would be JWT token verification
    const parts = token.split('_');
    return parts[2] || '1'; // Default to user 1 if parsing fails
  }

  /**
   * Validate mock access token
   */
  validateAccessToken(token: string): boolean {
    // Simple validation for mock tokens
    return token.startsWith('mock_access_') && token.split('_').length >= 5;
  }

  /**
   * Check if refresh token is valid
   */
  isRefreshTokenValid(token: string): boolean {
    return this.validRefreshTokens.has(token);
  }
}


